/* eslint-disable max-len */
import { destroySafe } from '@deqo/foundation';
import { Connection, Podopolis } from '@deqo/podopolis';
import React, { useEffect, useState } from 'react';
import { useEvent } from 'react-use';
import styled from 'styled-components';
import { useParams } from 'wouter';
import { uuid } from '../../CodesThatsGottaGoSomewhereElse';
import { Host } from '../../Host';
import { ApiClient } from '../../api/ApiClient';
import { getPermitToken } from '../../api/ApiEndpoints';
import { v2 } from '../../api/ApiInterfaceV2';
import { BoardData, BoardType } from '../../api/ApiTypes';
import { Monitor } from '../../platform/Monitor';
import { Fill } from '../../ui/Fill';
import { ContainerScope, resolve, useResolve } from '../Container';
import { Crash } from '../Crash';
import { PermissionCode } from '../PassportModel';
import { ProfileModel } from '../ProfileModel';
import { AccountModel } from '../account/AccountModel';
import { Editor } from '../canvas/Editor';
import { Flexer } from '../dashboard/elements/LayoutElements';
import { Float } from './../../ui';
import { EditorLoading } from './EditorLoading';
import { EditorOptions } from './EditorOptions';
import { EditorParams } from './EditorParams';
import { EditorPodSession } from './EditorPodSession';
import { StaticEditorPodSession } from './StaticEditorPodSession';
import { AskToJoinView } from './participants/AskToJoinView';

const Border = styled(Fill)`
    display: flex;
    flex-direction: column;
    background: #EFF3F6;
`;

const StatusOverlay = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    z-index: 50;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
`;

const StatusMessage = styled.div`
    padding: 2rem 4rem;
    border-radius: 1rem;
    font-size: 4rem;
    background: white;
    opacity: 0.5;
`;

const StatusInfo = styled.div`
    font-size: 1.5rem;
`;

const Retrying = () => {

    const podopolis = useResolve(Podopolis);
    const [tick, setTick] = useState(0);
    const [attempt, setAttempt] = useState(0);
    const [lastMouse, setLastMouse] = useState(performance.now());
    const [lastForce, setLastForce] = useState(performance.now());

    const idle = (performance.now() - lastMouse) > 1000 * 5;

    useEffect(() => podopolis.on('retrying', x => {
        setAttempt(x);
        console.log('Connection attempt', x);
    }), []);

    useEvent('mousemove', () => {
        setLastMouse(performance.now());
        // If we are on attempt 5 & 5s past since last, force retry on mouse move
        if (attempt >= 5 && ((performance.now() - lastForce) > 5000)) {
            setLastForce(performance.now());
            podopolis.retry();
        }
    });

    useEffect(() => {
        const interval = setInterval(() => setTick(tick + 1), 1000);
        return () => clearInterval(interval);
    }, []);

    return <StatusOverlay>
        <StatusMessage>
            {idle && <span>Waiting For Input</span>}
            {!idle && <span>Reconnecting</span>}
        </StatusMessage>
    </StatusOverlay>;
};

const loadingMessages = [
    'Fetching board info...',
    'Connecting to podopolis...',
    'Joining pod...',
    'Ready',
];

type LoaderCallback = (result: Omit<EditorParams, 'options'> | false) => void;

class Loader extends React.Component<{ boardId?: string, ui: boolean, callback: LoaderCallback }> {

    public static contextType = ContainerScope;

    public state = {
        instanceId: uuid(),
        boardData: null as BoardData,
        permissionsData: null as PermissionCode[],
        connection: null as Connection,
        podSession: null as EditorPodSession,
        stage: 0,
        error: null as Error,
    };

    @resolve(ApiClient) private api!: ApiClient;
    @resolve(Podopolis) private podopolis!: Podopolis;
    @resolve(AccountModel) private account!: AccountModel;

    public render() {
        if (this.state.error) return <Crash error={this.state.error} />;
        if (this.props.ui) return <EditorLoading message={loadingMessages[this.state.stage]} progress={this.state.stage / loadingMessages.length} />;
        return null;
    }

    public componentDidMount = () => this.loadBoardData();

    private loadBoardData = async () => {
        const { joined, reason } = await this.api.exec(v2.boards.join(this.props.boardId, false));
        if (!joined && reason === 'account_mismatch') {
            // If we are not "in" the same account as the board, reload the page
            window.location.reload();
        }
        else {
            const boardData = await this.api.exec(v2.boards.info(this.props.boardId));
            const permissionsData = await this.api.exec(v2.me.permissions(['board', this.props.boardId]));
            // Check we have at least read access
            if (permissionsData.includes('bo:read')) {
                if (boardData.type === BoardType.template || Host.offline) {
                    this.setState({ boardData, permissionsData, stage: 2 }, this.loadStaticPod);
                }
                else {
                    this.setState({ boardData, permissionsData, stage: 1 }, this.connect);
                }
            }
            else {
                // If not, callback with false to say we need permission
                this.props.callback(false);
            }
        }
    };

    private connect = async (r: number = 0) => {
        try {
            const connection = await this.podopolis.waitForActiveConnection();
            this.setState({ connection, stage: 2 }, this.joinPod);
        }
        catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            if (r < 5) {
                await this.connect(r + 1);
            }
        }
    };

    private joinPod = async () => {
        const { boardData, connection } = this.state;
        // TODO This should be somewhere else
        const { permitToken } = await this.api.exec(getPermitToken(boardData.podId, 'board'));
        const pod = await connection.join(boardData.podId, permitToken);
        const podSession = new EditorPodSession(pod);
        await podSession.start();
        // await waitFor(1000);
        this.setState({ podSession, stage: 3 }, this.onReady);
    };

    private loadStaticPod = async () => {
        const { boardData } = this.state;
        const snapshotData = await this.api.exec(v2.pods.snapshots(boardData.podId, 'latest'));
        const podSession = new StaticEditorPodSession(snapshotData as any);
        // await waitFor(1000);
        this.setState({ podSession, stage: 3 }, this.onReady);
    };

    private onReady = () => {
        this.props.callback({ ...this.state });
    };
}

type EditorPageStatus = 'loading' | 'reloading' | 'waiting' | 'connected' | 'unloaded' | 'refuse';

const alertsFor = ['swhitf@deqo.io', 'jamie@deqo.io', 'steve.moseley@deqo.io'];

class EditorHost extends React.Component<{ boardId?: string }> {

    public static contextType = ContainerScope;

    public state = {
        status: 'loading' as EditorPageStatus,
        params: null as EditorParams,
        options: null as EditorOptions,
    };

    public render() {
        const { status, params, options } = this.state;
        return <Border>
            {!!params && <Editor key={params.instanceId} params={{ ...params, options }} />}
            {(status === 'loading' || status === 'reloading') &&
                <Loader boardId={this.props.boardId} ui={status === 'loading'} callback={this.loaderCallback} />
            }
            {status === 'reloading' &&
                <Retrying />
            }
            {status === 'refuse' &&
                <Flexer flex grow center>
                    <Float inline shadow="sm">
                        <Flexer padding="1">
                            <AskToJoinView boardId={this.props.boardId} autoAsk={options.action === 'join'} callback={() => {
                                this.setState({
                                    status: 'loading',
                                    params: null,
                                    options: { ...options, action: null }, // Reset action to prevent autojoin loop
                                });
                            }} />
                        </Flexer>
                    </Float>
                </Flexer>
            }
        </Border>;
    }

    public componentDidMount() {
        this.setState({ options: EditorOptions.fromWindow() });
    }

    public componentWillUnmount() {
        destroySafe(this.state?.params?.podSession);
    }

    private loaderCallback = (params: EditorParams | false) => {
        if (params) {
            params.podSession.on('destroy', this.onPodSessionDestroy);
            this.setState({ status: 'connected', params });
        }
        else {
            this.setState({ status: 'refuse', params: null });
        }
    };

    private onPodSessionDestroy = async (reason: string) => {
        const me = this.context.get(ProfileModel);
        if (alertsFor.includes(me?.email) && !!reason) {
            alert(`BoardReloadError: ${reason}`);
        }
        if (!!reason && reason !== 'closed' && reason !== 'dropped') {
            Monitor.error('BoardReloadError', null, { reason });
            this.setState({ status: 'reloading' });
        }
        else if (!!reason) {
            this.setState({ status: 'reloading' });
        }
        else {
            this.setState({ status: 'unloaded', params: null });
        }
        return Promise.resolve();
    };
}

export const EditorPage = () => {
    const { entityId } = useParams<{ entityId: string }>();
    return <>
        <EditorHost key={entityId} boardId={entityId} />
    </>;
};
